import { AxiosError } from "axios";
import { api } from "@/shared/api/client";
import type { SupportSettings, SupportSettingsUpdate } from "./types";
import { mockGetSettings, mockUpdateSettings } from "./mock";

// ── Mock toggle ──────────────────────────────────────────────────────────────
// Same convention as the customers feature: until the backend endpoint is live the
// UI runs against an in-memory mock mirroring the exact contract. FLIP TO REAL:
// VITE_USE_MOCK=false — no other change needed.
const USE_MOCK =
  (import.meta.env.VITE_USE_MOCK ?? "true").toString().toLowerCase() !== "false";

// Small latency so the loading skeleton is exercised in mock mode.
const delay = (ms = 250) => new Promise<void>((r) => setTimeout(r, ms));

// ── Error mapping boundary ───────────────────────────────────────────────────
// The update serializer returns DRF field errors ({ premium_price: [...] } /
// { support_telegram_link: [...] }) on a 400, or a top-level { message } for the
// empty-body case. Normalise to a stable per-field code at this one boundary so the
// cards never branch on raw server strings.
export type SettingsErrorCode =
  | "invalidPrice"
  | "invalidUrl"
  | "invalidPhone"
  | "invalidMonths"
  | "generic";

export class SettingsError extends Error {
  constructor(public code: SettingsErrorCode) {
    super(code);
    this.name = "SettingsError";
  }
}

function toSettingsError(e: unknown): SettingsError {
  // Mock path tags the thrown Error with `settingsCode`; real path is an AxiosError 400.
  const tagged = (e as { settingsCode?: SettingsErrorCode }).settingsCode;
  if (tagged) return new SettingsError(tagged);
  if (e instanceof AxiosError && e.response?.status === 400) {
    const data = e.response.data as Record<string, unknown> | undefined;
    if (data && "premium_price" in data) return new SettingsError("invalidPrice");
    if (data && "support_telegram_link" in data) return new SettingsError("invalidUrl");
    if (data && "support_phone" in data) return new SettingsError("invalidPhone");
    if (data && "free_tier_months" in data) return new SettingsError("invalidMonths");
  }
  return new SettingsError("generic");
}

export async function getSettings(): Promise<SupportSettings> {
  if (USE_MOCK) {
    await delay();
    return mockGetSettings();
  }
  const res = await api.get<SupportSettings>("/support/settings/");
  return res.data;
}

export async function updateSettings(
  patch: SupportSettingsUpdate,
): Promise<SupportSettings> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockUpdateSettings(patch);
    } catch (e) {
      throw toSettingsError(e);
    }
  }
  try {
    const res = await api.patch<SupportSettings>("/support/settings/", patch);
    return res.data;
  } catch (e) {
    throw toSettingsError(e);
  }
}
